<template>
    <!--
        "You've already completed this packet" — shown by UrlRedirect when every form in a packet
        already has a submission for this patient inside the resume window. Re-rendering the packet
        would make the patient re-do finished sections AND file a duplicate PDF into Open Dental
        imaging for every one, so this page is a dead end on purpose: no button, no re-run.

        Card shape is LinkNotFound's, with a green check header instead of the amber warning.
        The packet name is its own {{ }} node — never v-html, never through t(). See
        libraries/packet-progress.js.
    -->
    <div class="packet-complete-page" data-test="packet-complete">
        <div class="packet-complete-container">
            <div class="complete-card">
                <div v-if="logoUrl" class="complete-logo-strip">
                    <img :src="logoUrl" alt="Practice logo" class="practice-logo" />
                </div>
                <div class="complete-card-header">
                    <div class="complete-icon">
                        <i class="fa fa-check-circle"></i>
                    </div>
                    <h1 class="complete-title">{{ title }}</h1>
                </div>
                <div class="complete-card-body">
                    <p v-if="displayName" class="complete-packet-name" data-test="packet-complete-name">{{ displayName }}</p>
                    <p class="complete-message">{{ message }}</p>
                    <p class="complete-hint">{{ hint }}</p>
                    <div v-if="hasOffice" class="complete-office" data-test="packet-complete-office">
                        <p v-if="siteName" class="office-name">{{ siteName }}</p>
                        <p v-if="sitePhone" class="office-phone">{{ sitePhone }}</p>
                    </div>
                </div>
            </div>
            <PoweredByFooter />
        </div>
    </div>
</template>

<script>
import PoweredByFooter from '@/components/PoweredByFooter.vue';
import { t } from '../utils/systemTranslations';

export default {
    name: 'PacketComplete',

    components: {
        PoweredByFooter
    },

    props: {
        packetName: { type: String, default: '' },
        logoUrl: { type: String, default: null },
        siteName: { type: String, default: null },
        sitePhone: { type: String, default: null },
        language: { type: String, default: 'en' }
    },

    computed: {
        displayName() {
            return (this.packetName || '').trim();
        },

        /**
         * An older forms-service response carries neither siteName nor sitePhone. Rendering an empty
         * bordered block under "contact the office" reads as a broken page, so the whole block goes.
         */
        hasOffice() {
            return !!((this.siteName || '').trim() || (this.sitePhone || '').trim());
        },

        title() {
            return t('packetCompleteTitle', this.language);
        },

        message() {
            return t('packetCompleteMessage', this.language);
        },

        hint() {
            return t('packetCompleteHint', this.language);
        }
    }
};
</script>

<style scoped>
.packet-complete-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px;
    font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, sans-serif;
}

.packet-complete-container {
    width: 100%;
    max-width: 440px;
}

.complete-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    overflow: hidden;
}

.complete-logo-strip {
    background: white;
    padding: 20px 24px 0 24px;
    text-align: center;
}

/* Height cap is mandatory: one practice's LogoUrl is an 800x800 headshot.
   max-width alone is not a cap. Enforced by cypress/node-tests/logo-size-cap-guardrail.test.js. */
.practice-logo {
    max-height: 64px;
    max-width: 220px;
    object-fit: contain;
}

.complete-card-header {
    background: linear-gradient(135deg, #3abf94 0%, #0f7259 100%);
    padding: 32px;
    text-align: center;
}

.complete-icon {
    font-size: 48px;
    color: white;
    margin-bottom: 16px;
}

.complete-title {
    color: white;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    line-height: 1.3;
}

.complete-card-body {
    padding: 32px;
    text-align: center;
}

.complete-packet-name {
    font-size: 1.0625rem;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.4;
    margin-bottom: 12px;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.complete-message {
    font-size: 1rem;
    color: #334155;
    line-height: 1.6;
    margin-bottom: 12px;
}

.complete-hint {
    font-size: 0.9375rem;
    color: #64748b;
    line-height: 1.6;
    margin: 0;
}

.complete-office {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
}

.office-name {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #334155;
    margin: 0 0 4px 0;
}

.office-phone {
    font-size: 0.9375rem;
    color: #64748b;
    margin: 0;
}

@media (max-width: 480px) {
    .packet-complete-page {
        padding: 16px;
    }

    .complete-card-header {
        padding: 24px;
    }

    .complete-title {
        font-size: 1.25rem;
    }

    .complete-card-body {
        padding: 24px;
    }

    .practice-logo {
        max-height: 48px;
        max-width: 180px;
    }
}
</style>
